import React, { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import "./AccountManagement.css";
import HeaderMain from "../../../components/Header/HeaderMain";
import Footer from "../../../components/Footer/Footer";
import NewsLetter from "../../../components/NewsLetter/NewsLetter";
import {
  FaUserShield,
  FaClipboardCheck,
  FaPaintBrush,
  FaChartLine,
  FaBullhorn,
  FaBoxes,
} from "react-icons/fa";
import { marketplaces, contact } from "./accountData";
import {
  SectionHead,
  WhyChooseUs,
  StatsBand,
  OtherMarketplaces,
  FinalCta,
  MarketplaceIcon,
} from "./AccountParts";

const CORE_ICONS = [
  FaUserShield,
  FaClipboardCheck,
  FaPaintBrush,
  FaChartLine,
  FaBullhorn,
  FaBoxes,
];

// Renders a numbered card grid (used for listing / advertising / inventory / pricing).
const NumberedGrid = ({ items, cols = "am-grid-3" }) => (
  <div className={`am-grid ${cols}`}>
    {items.map((item, i) => (
      <div className="am-card" data-aos="zoom-in" key={item.title}>
        <span className="am-num">{String(i + 1).padStart(2, "0")}</span>
        <h4>{item.title}</h4>
        <p>{item.desc}</p>
      </div>
    ))}
  </div>
);

const MarketplaceAccount = () => {
  const { marketplace } = useParams();
  const data = marketplaces[marketplace];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [marketplace]);

  // Unknown marketplace slug → send back to the hub (never render a blank page).
  if (!data) {
    return <Navigate to="/services/account-management" replace />;
  }

  return (
    <div className="am-page">
      <HeaderMain />

      {/* Breadcrumb */}
      <div className="am-wrap">
        <div className="am-breadcrumb">
          <Link to="/">Home</Link> /{" "}
          <Link to="/services/account-management">Account Management</Link> /{" "}
          <span>{data.name}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="am-hero am-wrap">
        <div className="am-hero-inner">
          <div className="am-hero-text" data-aos="fade-right">
            <span className="am-eyebrow">{data.name} Seller Services</span>
            <h1>
              {data.name} Seller <span>Account Management</span> Services
            </h1>
            <p className="am-tagline">{data.tagline}</p>
            <p className="am-lead">{data.hero}</p>
            <div className="am-cta-row">
              <a className="am-btn am-btn-primary" href={contact.phoneTel}>
                Get Started
              </a>
              <a
                className="am-btn am-btn-outline"
                href={contact.whatsapp}
                target="_blank"
                rel="noreferrer"
              >
                Enquire Now
              </a>
            </div>
          </div>
          <div className="am-hero-art" data-aos="fade-left">
            <div className="am-hero-badge">
              <div className="am-hero-icon">
                <MarketplaceIcon icon={data.icon} />
              </div>
              <h3>{data.name} Experts</h3>
              <p>
                End-to-end {data.name} account handling — listing, ads,
                inventory, pricing and support under one roof.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="am-section am-wrap">
        <SectionHead
          eyebrow="Overview"
          title={`Grow Faster on ${data.name}`}
        />
        <div className="am-richtext" data-aos="fade-up">
          {data.intro.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      </section>

      {/* Four pillars (Amazon-style) — only when provided */}
      {data.pillars ? (
        <section className="am-section am-wrap">
          <SectionHead
            eyebrow="What We Do"
            title={`Our ${data.name} Service Pillars`}
            intro={`The four foundations of a healthy, growing ${data.name} account.`}
          />
          <div className="am-grid am-grid-4">
            {data.pillars.map((p, i) => {
              const Icon = CORE_ICONS[i % CORE_ICONS.length];
              return (
                <div className="am-card" data-aos="zoom-in" key={p.title}>
                  <div className="am-card-icon">
                    <Icon />
                  </div>
                  <h4>{p.title}</h4>
                  <p>{p.desc}</p>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Why choose us */}
      <WhyChooseUs name={data.name} />

      {/* Core services */}
      <section className="am-section am-wrap">
        <SectionHead
          eyebrow="Core Services"
          title={`${data.name} Account Management Services`}
          intro={`Everything your ${data.name} store needs to launch, rank and scale — managed end to end by our team.`}
        />
        <div className="am-grid am-grid-3">
          {data.coreServices.map((s, i) => {
            const Icon = CORE_ICONS[i % CORE_ICONS.length];
            return (
              <div className="am-card" data-aos="zoom-in" key={s.title}>
                <div className="am-card-icon">
                  <Icon />
                </div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Stats band */}
      <StatsBand />

      {/* Listing optimization */}
      <section className="am-section am-wrap">
        <SectionHead
          eyebrow="Listing Optimization"
          title={`${data.name} Product Listing Optimization`}
          intro="Precise, keyword-driven cataloguing that earns top search positions and converts the traffic it attracts."
        />
        <NumberedGrid items={data.listingOptimization} />
      </section>

      {/* Advertising */}
      <section className="am-section am-wrap">
        <SectionHead
          eyebrow="Advertising"
          title={`${data.name} Advertising Management`}
          intro="Full-funnel ad management engineered for visibility, profitable ROI and the lowest possible advertising cost of sale."
        />
        <NumberedGrid items={data.advertising} />
      </section>

      {/* Inventory */}
      <section className="am-section am-wrap">
        <SectionHead
          eyebrow="Inventory"
          title={`${data.name} Inventory Management`}
          intro="Always in stock, never over-stocked — disciplined inventory control that protects your account health and your margin."
        />
        <NumberedGrid items={data.inventory} />
      </section>

      {/* Pricing */}
      <section className="am-section am-wrap">
        <SectionHead
          eyebrow="Pricing Strategy"
          title={`${data.name} Pricing Strategy`}
          intro="Competitive, profitable pricing built on a complete understanding of every marketplace fee."
        />
        <NumberedGrid items={data.pricing} />
      </section>

      {/* Extra deep-dive sections */}
      {data.extraSections && data.extraSections.length ? (
        <section className="am-section am-wrap">
          <SectionHead eyebrow="Going Further" title={`More ${data.name} Expertise`} />
          <div className="am-grid am-grid-2">
            {data.extraSections.map((sec) => (
              <div className="am-extra-block" data-aos="fade-up" key={sec.title}>
                <h3>{sec.title}</h3>
                <p>{sec.body}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Other marketplaces */}
      <OtherMarketplaces currentSlug={data.slug} />

      {/* Final CTA */}
      <FinalCta
        heading={`Ready to Scale on ${data.name}?`}
        text={`Let our ${data.name} experts audit your account for free and build a clear, data-driven plan to grow your sales. Fill out the form and we'll be in touch.`}
      />

      <NewsLetter />
      <Footer />
    </div>
  );
};

export default MarketplaceAccount;
